import { useSelect } from '@wordpress/data';
import { useEffect, useState, useMemo, useCallback } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import PanelSettings from './advance/inspector-control';
import Advance from './advance';
import { VayuBlocksDevice } from '@vayu-blocks/device-control';
import { VideoHtml, MouseFollower, GetIcon,CopyPasteStyles } from '@vayu-blocks';
import { VayuBlocksUniqueId } from '@vayu-blocks/global';
import { RichText, BlockControls } from '@wordpress/block-editor';
import clsx from 'clsx';
import {getWrapperStyle,getTitleGapStyle,getTitleTextStyle,
	getIconStyle,getContentStyle,getContentLiStyle,
} from './advance/inline-style';
import { useRef } from 'react';

const getAllHeadingBlocks = (blocks) => {
	let headingBlocks = [];

	const traverse = (blocks) => {
		blocks.forEach((block) => {
			const { name, attributes = {}, clientId, innerBlocks = [] } = block;

			// ✅ Only if block name includes "heading"
			if (name?.toLowerCase().includes('heading')) {
				let rawContent =
					attributes?.content ||
					attributes?.headingTitle ||
					attributes?.richText ||
					attributes?.text ||
					'';

				if (typeof rawContent !== 'string') {
					try {
						rawContent = String(rawContent || '');
					} catch {
						rawContent = '';
					}
				}

				let headingLevel = null;
				let cleanText = '';

				try {
					const parser = new DOMParser();
					const doc = parser.parseFromString(rawContent, 'text/html');
					const hNode = doc.body.querySelector('h1,h2,h3,h4,h5,h6');

					if (hNode) {
						headingLevel = parseInt(hNode.tagName.replace('H', ''), 10);
						cleanText = hNode.textContent?.trim() || '';
					}
				} catch {
					// nothing to do here, will fallback below
				}

				// 🔁 If no <h*> tag was found
				if (!headingLevel) {
					if (attributes?.headingTag) {
						headingLevel = parseInt(attributes.headingTag.replace('h', ''), 10);
					} else if (typeof attributes?.level === 'number') {
						headingLevel = attributes.level;
					} else if (attributes?.tagName) {
						headingLevel = parseInt(attributes.tagName.replace('h', ''), 10);
					} else if (attributes?.tag) {
						headingLevel = parseInt(attributes.tag.replace('h', ''), 10);
					} else {
						headingLevel = 2; // ultimate fallback
					}
				}

				// 🧹 Clean text fallback if not from hNode
				if (!cleanText && rawContent) {
					const tempDiv = document.createElement('div');
					tempDiv.innerHTML = rawContent;
					cleanText = tempDiv.textContent?.trim() || '';
				}

				const headingId =
					attributes?.anchor ||
					attributes?.block_id ||
					attributes?.uniqueID ||
					`heading-${clientId}`;

				if (cleanText) {
					headingBlocks.push({
						id: headingId,
						text: cleanText,
						level: headingLevel,
					});
				}
			}

			if (innerBlocks.length > 0) {
				traverse(innerBlocks);
			}
		});
	};

	traverse(blocks);
	return headingBlocks;
};

const renderNestedList = (
	items,
	handleLinkClick,
	contentliStyle,
	currentLevel = 0,
	markerStyle = 'disc',
	parentIndexPath = []
) => {
	if (!items.length) return null;

	const result = [];
	let i = 0;

	while (i < items.length) {
		const current = items[i];
		const children = [];
		const currentIndex = result.length + 1; // 1-based index like 1,2,3...

		i++;

		while (i < items.length && items[i].level > current.level) {
			children.push(items[i]);
			i++;
		}

		const Tag = markerStyle === 'decimal' ? 'ol' : 'ul';
		const indexPath = [...parentIndexPath, currentIndex];

		result.push(
			<li key={current.id} className={`vb-toc-item level-${current.level}`}>
				<a
					href={`#${current.id}`}
					onClick={(e) => handleLinkClick(e, current.id)}
					style={contentliStyle}
				>
					{markerStyle === 'decimal' ? `${indexPath.join('.')}. ` : ''}
					{current.text}
				</a>

				{children.length > 0 && (
					<Tag>
						{renderNestedList(
							children,
							handleLinkClick,
							contentliStyle,
							current.level + 1,
							markerStyle,
							indexPath
						)}
					</Tag>
				)}
			</li>
		);
	}

	return result;
};

const renderFlatList = (
	items,
	handleLinkClick,
	contentliStyle,
	markerStyle = 'disc'
) => {
	if (!items.length) return null;

	const Tag = markerStyle === 'decimal' ? 'ol' : 'ul';

	return items.map((item, index) => (
		<li key={item.id} className={`vb-toc-item level-${item.level}`}>
			<a
				href={`#${item.id}`}
				onClick={(e) => handleLinkClick(e, item.id)}
				style={contentliStyle}
			>
				{markerStyle === 'decimal' ? `${index + 1}. ` : ''}
				{item.text}
			</a>
		</li>
	));
};

const renderLimitedNestedList = (
	items,
	handleLinkClick,
	contentliStyle,
	markerStyle = 'disc',
	parentIndexPath = [],
	maxDepth = 2
) => {
	if (!items.length) return null;

	const result = [];
	let i = 0;

	while (i < items.length) {
		const current = items[i];
		const children = [];
		const currentIndex = result.length + 1;
		const indexPath = [...parentIndexPath, currentIndex];

		i++;

		while (
			i < items.length &&
			items[i].level > current.level &&
			indexPath.length < maxDepth
		) {
			children.push(items[i]);
			i++;
		}

		const Tag = markerStyle === 'decimal' ? 'ol' : 'ul';

		result.push(
			<li key={current.id} className={`vb-toc-item level-${current.level}`}>
				<a
					href={`#${current.id}`}
					onClick={(e) => handleLinkClick(e, current.id)}
					style={contentliStyle}
				>
					{markerStyle === 'decimal' ? `${indexPath.join('.')}. ` : ''}
					{current.text}
				</a>

				{children.length > 0 && (
					<Tag>
						{renderLimitedNestedList(
							children,
							handleLinkClick,
							contentliStyle,
							markerStyle,
							indexPath,
							maxDepth
						)}
					</Tag>
				)}
			</li>
		);
	}

	return result;
};

const renderDeepNumberedList = (
	items,
	handleLinkClick,
	contentliStyle,
	markerStyle = 'decimal',
	levelCounts = []
) => {
	if (!items.length) return null;

	const result = [];
	let i = 0;

	while (i < items.length) {
		const current = items[i];
		const children = [];

		i++;
		while (i < items.length && items[i].level > current.level) {
			children.push(items[i]);
			i++;
		}

		// Ensure levelCounts has enough entries
		while (levelCounts.length < current.level) {
			levelCounts.push(0);
		}

		// Remove deeper levels
		levelCounts.splice(current.level);

		// Increment current level count
		levelCounts[current.level - 1] = (levelCounts[current.level - 1] || 0) + 1;

		// Remove any leading 0s (e.g., if H2 is the first heading)
		const displayCounts = levelCounts.slice(0, current.level);
		while (displayCounts[0] === 0) {
			displayCounts.shift();
		}

		const numbering = displayCounts.join('.');

		result.push(
			<li key={current.id} className={`vb-toc-item level-${current.level}`}>
				<a
					href={`#${current.id}`}
					onClick={(e) => handleLinkClick(e, current.id)}
					style={contentliStyle}
				>
					{markerStyle === 'decimal' ? `${numbering}. ` : ''}
					{current.text}
				</a>

				{children.length > 0 && (
					<ul>
						{renderDeepNumberedList(
							children,
							handleLinkClick,
							contentliStyle,
							markerStyle,
							[...levelCounts]
						)}
					</ul>
				)}
			</li>
		);
	}

	return result;
};

const edit = ({ attributes, setAttributes, clientId, isSelected }) => {

	const view = VayuBlocksDevice();
	const { enableCollapse, collapseBehavior, collapseLimit } = attributes;

	VayuBlocksUniqueId({ setAttributes, clientId });

	const [openState, setOpenState] = useState(true);
	const [iconhovered, setIconHovered] = useState(false);

	// Fetch all heading blocks - note: dependency [] means update only on mount & block editor updates
	const headings = useSelect(
		(select) => {
			const blocks = select('core/block-editor').getBlocks();
			return getAllHeadingBlocks(blocks);
		},
		[attributes.heading] 
	);

	// Memoize enabled heading levels
	const enabledLevels = useMemo(() => {
		return Object.entries(attributes.heading || {})
			.filter(([_, isEnabled]) => isEnabled)
			.map(([level]) => parseInt(level.replace('h', ''), 10));
	}, [attributes.heading]);

	// Filter headings by enabled levels
	const filteredHeadings = useMemo(() => {
		return headings.filter((h) => enabledLevels.includes(h.level));
	}, [headings, enabledLevels]);

	// Calculate visible and extra list with memoization to avoid useEffect + state updates
	const [visibleList, extraList] = useMemo(() => {
		if (!enableCollapse) {
			return [filteredHeadings, []];
		}

		const limit = Math.max(0, parseInt(collapseLimit || 0, 10));
		return [filteredHeadings.slice(0, limit), filteredHeadings.slice(limit)];
	}, [filteredHeadings, collapseLimit, enableCollapse]);

	// Update open state on collapseBehavior or enableCollapse change
	useEffect(() => {
		if (enableCollapse) {
			setOpenState(collapseBehavior === 'open');
		}
	}, [collapseBehavior, enableCollapse]);

	const didMountRef = useRef(false);

	useEffect(() => {
	if (didMountRef.current) {
		if (enableCollapse) {
			setOpenState(false);
		}
	} else {
		didMountRef.current = true;
	}
	}, [collapseLimit]);

	// Memoized click handler for smooth scroll
	const handleLinkClick = useCallback((e, id) => {
		e.preventDefault();
		const target = document.getElementById(id);
		if (target) {
			target.scrollIntoView({ behavior: 'smooth' });
		}
	}, []);

	const shrinkWhenCollapsed = enableCollapse && !openState && attributes.enableShrink;

	const wrapperClass = clsx(
		'vb-toc-wrapper',
		`columns-${attributes.columns || 1}`,
		`marker-${attributes.markerStyle}`,
		`separator-${attributes.separator}`,
	);

	const wrapperStyle = getWrapperStyle(attributes, view);
	const titlegapStyle = getTitleGapStyle(attributes, view);
	const titletextstyle = getTitleTextStyle(attributes, view);
	const iconStyle = getIconStyle(attributes, view, openState, iconhovered);
	const contentStyle = getContentStyle(attributes, view);
	const contentliStyle = getContentLiStyle(attributes, view);

	const ListTag = attributes.markerStyle === 'decimal' ? 'ol' : 'ul';

	const renderedList = useMemo(() => {
		const fullList = [...visibleList, ...(openState ? extraList : [])];

		if (attributes.hierarchyType === 'flat') {
			return renderFlatList(fullList, handleLinkClick, contentliStyle, attributes.markerStyle);
		} else if (attributes.hierarchyType === 'limited') {
			return renderLimitedNestedList(fullList, handleLinkClick, contentliStyle, attributes.markerStyle, [], 2);
		} else if (attributes.hierarchyType === 'deep-numbered') {
			return renderDeepNumberedList(fullList, handleLinkClick, contentliStyle, attributes.markerStyle);
		} else {
			return renderNestedList(fullList, handleLinkClick, contentliStyle, 0, attributes.markerStyle, []);
		}
	}, [visibleList, extraList, openState, attributes.hierarchyType, attributes.markerStyle]);

	return (
		<>

			{isSelected && (
				<BlockControls>
					<CopyPasteStyles
						attributes={attributes}
						setAttributes={setAttributes}
						blockName={'vayu-blocks/table-of-contents'}
					/>
				</BlockControls>
			)}
		
			<PanelSettings
				attributes={attributes}
				setAttributes={setAttributes}
				isSelected={isSelected}
				clientId={clientId}
			/>

			<Advance attributes={attributes} clientId={clientId}>
				<VideoHtml bg={attributes.advBackground} />

				<div className={wrapperClass} style={wrapperStyle}>
					<div className="vb-toc-title" style={titlegapStyle}>
						{attributes.overallalignment === 'left' && enableCollapse && (
							<button
								type="button"
								onClick={() => setOpenState((prev) => !prev)}
								className="vb-toc-toggle-button"
								style={iconStyle}
								aria-expanded={openState}
								onMouseEnter={() => setIconHovered(true)}
								onMouseLeave={() => setIconHovered(false)}
							>
								<span>{openState
											? attributes.activetext || ''
											: attributes.inactivetext || ''}</span>
								<GetIcon
									value={
										openState
											? attributes.activeiconName || ''
											: attributes.inactiveiconName || ''
									}
								/>
							</button>
						)}

						<RichText
							tagName="div"
							className="vb-toc-title-content"
							placeholder={__('Table Of Contents', 'vayu-blocks')}
							value={attributes.tocTitle}
							onChange={(value) => setAttributes({ tocTitle: value })}
							style={titletextstyle}
						/>

						{attributes.overallalignment === 'right' && enableCollapse && (
							<button
								type="button"
								onClick={() => setOpenState((prev) => !prev)}
								className="vb-toc-toggle-button"
								style={iconStyle}
								aria-expanded={openState}
								onMouseEnter={() => setIconHovered(true)}
								onMouseLeave={() => setIconHovered(false)}
							>
								<span>{openState
											? attributes.activetext || ''
											: attributes.inactivetext || ''}</span>
								<GetIcon
									value={
										openState
											? attributes.activeiconName || ''
											: attributes.inactiveiconName || ''
									}
								/>
							</button>
						)}
					</div>

					{(visibleList.length > 0 || (openState && extraList.length > 0)) ? (
						<>
							{attributes.separator !== 'none' && (
								<hr
									className={`vb-toc-separator separator-${attributes.separator}`}
								/>
							)}

							<ListTag className="vb-toc-list" style={contentStyle}>
								{renderedList}
							</ListTag>
							
						</>
					) : (
						openState && (
							<p className="vb-toc-placeholder">
								{__('Add a header to begin generating the table of contents.', 'vayu-blocks')}
							</p>
						)
					)}
				</div>

				<MouseFollower value={attributes.advFollower} clientId={clientId} />
			</Advance>
		</>
	);
};

export default edit;